import { MultiTableAnalysisWidgetData } from '../types/gotypes'
import { REGENERATE_NOT_PERMITTED_CODE } from './dashboardOwnership'
import { isAiRefusalCode } from './aiAccess'

/**
 * Pure state logic for a "Regenerate AI Insights" bulk run.
 *
 * The dashboard editor starts one analysis per multi-table analysis widget and
 * then polls each analysis's `/progress` endpoint. Everything here is a pure
 * function so the awkward parts — retrying a subset without losing the cards
 * still in flight, ignoring a poll response about a superseded analysis, and
 * giving up on an analysis that has gone — are testable without a browser.
 */

/**
 * #1219 gate 2: `refused` is its own terminal state, distinct from `failed`.
 * A regenerate refused for lacking overwrite authority queued nothing and
 * cannot succeed on a bare retry — the editor's "Retry failed" must not
 * offer it, and the summary must not count it as a failure.
 */
export type BulkRunItemState = 'starting' | 'pending' | 'running' | 'completed' | 'failed' | 'refused'

export interface BulkRunItem {
  /** Stable identity for a card across retries. A widget's `multi_table_id` is
   *  not unique on its own (the same multi-table can appear on two cards), so
   *  the key carries the widget's position in the run that created it. */
  key: string
  widget: MultiTableAnalysisWidgetData
  /** The filter map of the page the card is on (#1810), sent as
   *  `job_filters`. Run All spans pages, so each card carries its own. */
  jobFilters?: Record<string, string>
  analysisId?: string
  state: BulkRunItemState
  error?: string
  /** Consecutive progress-poll failures. Bounded so a deleted (404) analysis
   *  stops being polled instead of polling until the page unmounts. */
  pollFailures: number
}

export interface BulkRunState {
  label: string
  items: BulkRunItem[]
}

/** Shape of a settled `axios.post(.../analysis/start)` result. */
export interface BulkRunStartResult {
  status: 'fulfilled' | 'rejected'
  value?: { data?: { analysis?: { id?: string; state?: string } } }
  reason?: {
    message?: string
    // `code` (#1219 gate 2): the machine-readable reason a rejection carries
    // alongside `error` — `regenerate_not_permitted` classifies as `refused`
    // rather than `failed`.
    response?: { status?: number; data?: { analysis?: { id?: string; state?: string }; error?: string; code?: string } }
  }
}

/** Shape of one settled `GET .../analysis/:id/progress` poll. */
export interface BulkRunPollOutcome {
  key: string
  /** The analysis the poll asked about — used to drop responses about an
   *  analysis the item has since moved off. */
  analysisId: string
  ok: boolean
  state?: string
  error?: string
}

/** Consecutive poll failures tolerated before an item is declared failed. */
export const MAX_CONSECUTIVE_POLL_FAILURES = 5

export const isTerminalBulkRunState = (state: BulkRunItemState): boolean =>
  state === 'completed' || state === 'failed' || state === 'refused'

export const hasActiveBulkRunItems = (items: BulkRunItem[]): boolean =>
  items.some(item => !isTerminalBulkRunState(item.state))

export interface BulkRunCounts {
  total: number
  completed: number
  failed: number
  /** #1219 gate 2: refused for lacking overwrite authority — counted apart
   *  from `failed` since "Retry failed" must never offer these. */
  refused: number
  inFlight: number
}

export function bulkRunCounts(items: BulkRunItem[]): BulkRunCounts {
  const completed = items.filter(i => i.state === 'completed').length
  const failed = items.filter(i => i.state === 'failed').length
  const refused = items.filter(i => i.state === 'refused').length
  return { total: items.length, completed, failed, refused, inFlight: items.length - completed - failed - refused }
}

/** One card to run, with the filter map of the page it is on (#1810). */
export interface BulkRunTarget {
  widget: MultiTableAnalysisWidgetData
  jobFilters?: Record<string, string>
}

export function makeBulkRunItems(targets: BulkRunTarget[]): BulkRunItem[] {
  return targets.map(({ widget, jobFilters }, index) => ({
    key: `${index}:${widget.multi_table_id || ''}`,
    widget,
    jobFilters,
    state: 'starting' as BulkRunItemState,
    pollFailures: 0,
  }))
}

/** Put failed items back into 'starting' so they can be re-submitted while
 *  keeping their key (and therefore their place in the existing run). */
export function resetItemsForRetry(items: BulkRunItem[]): BulkRunItem[] {
  return items.map(item => ({
    ...item,
    state: 'starting' as BulkRunItemState,
    analysisId: undefined,
    error: undefined,
    pollFailures: 0,
  }))
}

/**
 * Fold the settled start requests back into the submitted items.
 *
 * `freshKeys` are the items that actually queued a NEW analysis. A 409 means
 * the previous analysis is still pending/running and the response carries that
 * OLD analysis — nothing was queued, so the caller must not treat those cards
 * as regenerated (in particular it must not clear their manual edits).
 */
export function applyStartResults(
  items: BulkRunItem[],
  results: BulkRunStartResult[]
): { items: BulkRunItem[]; freshKeys: string[] } {
  const freshKeys: string[] = []
  const next = items.map((item, index) => {
    const result = results[index]
    if (!result) return item
    if (result.status === 'fulfilled') {
      const analysis = result.value?.data?.analysis
      if (analysis?.id) freshKeys.push(item.key)
      return {
        ...item,
        analysisId: analysis?.id,
        state: (analysis?.state as BulkRunItemState) || 'pending',
        error: undefined,
        pollFailures: 0,
      }
    }
    const existing = result.reason?.response?.data?.analysis
    if (existing?.id) {
      return {
        ...item,
        analysisId: existing.id,
        state: (existing.state as BulkRunItemState) || 'running',
        error: undefined,
        pollFailures: 0,
      }
    }
    // #1219 gate 2: a refusal for lacking overwrite authority is its own
    // terminal state — nothing was queued, and unlike a transient failure a
    // bare retry cannot ever succeed against it. An AI access refusal (#1537,
    // gate 1) and a used team budget (#1226) are the same kind of answer.
    const code = result.reason?.response?.data?.code
    const refused = code === REGENERATE_NOT_PERMITTED_CODE || isAiRefusalCode(code)
    return {
      ...item,
      state: (refused ? 'refused' : 'failed') as BulkRunItemState,
      error: result.reason?.response?.data?.error || result.reason?.message || 'Failed to start',
    }
  })
  return { items: next, freshKeys }
}

/**
 * Merge an updated subset back into the run instead of replacing it. Retrying
 * 2 failed cards out of 10 must leave the other 8 in the strip, still polling.
 */
export function mergeBulkRunItems(existing: BulkRunItem[], updated: BulkRunItem[]): BulkRunItem[] {
  const byKey = new Map(updated.map(item => [item.key, item]))
  const merged = existing.map(item => byKey.get(item.key) ?? item)
  const known = new Set(existing.map(item => item.key))
  for (const item of updated) {
    if (!known.has(item.key)) merged.push(item)
  }
  return merged
}

/**
 * Apply poll responses to the CURRENT items (not the snapshot the poll was
 * built from). An outcome is ignored when the item has moved on to a different
 * analysis or already reached a terminal state, so a slow response cannot flip
 * a completed card back to running.
 */
export function applyPollOutcomes(
  items: BulkRunItem[],
  outcomes: BulkRunPollOutcome[],
  maxConsecutiveFailures: number = MAX_CONSECUTIVE_POLL_FAILURES
): BulkRunItem[] {
  const byKey = new Map(outcomes.map(outcome => [outcome.key, outcome]))
  return items.map(item => {
    const outcome = byKey.get(item.key)
    if (!outcome) return item
    if (item.analysisId !== outcome.analysisId) return item
    if (isTerminalBulkRunState(item.state)) return item
    if (!outcome.ok) {
      const pollFailures = item.pollFailures + 1
      if (pollFailures >= maxConsecutiveFailures) {
        return {
          ...item,
          pollFailures,
          state: 'failed' as BulkRunItemState,
          error: 'Lost track of this analysis',
        }
      }
      return { ...item, pollFailures }
    }
    return {
      ...item,
      pollFailures: 0,
      state: (outcome.state as BulkRunItemState) || item.state,
      error: outcome.error,
    }
  })
}

/** Stop waiting on everything still running once the poll budget is spent. */
export function markBulkRunGaveUp(
  items: BulkRunItem[],
  message = 'Timed out waiting for this analysis to finish'
): BulkRunItem[] {
  return items.map(item =>
    isTerminalBulkRunState(item.state)
      ? item
      : { ...item, state: 'failed' as BulkRunItemState, error: message }
  )
}
